import axios from 'axios';
import { RequestConfig } from '@/../types/interface/config';
import { getCookie, getStorage, removeCookie } from '@lhb/cache';
import { isLarkEnv, logout } from '@/common/utils/ways';
import { message as AntdMessage } from 'antd';
import { URL_REG } from '@lhb/regexp';
import { deepCopy, isDef, matchQuery } from '@lhb/func';
import { notifyWechat } from '../api/common';
import { handleInterceptInLark } from '../utils/lark';

// MockUrl
const mockUrl = 'https://yapi.lanhanba.com/mock/';
// const baseUrl = '/api';

// 默认前缀加上
const applicationFlow = `/terra/api`;
const applicationCheckSpot = `/nox`;
const applicationMirage = `/mirage/api`;
const applicationZeus = `/zeus-api/api`;
const applicationLcn = `/lcn-api/api`;
const applicationFlowV2 = `/terra/v2`;
const appRes = `/res/api`;
const appTrust = `/trust-api/api`;
// const applicationLocal = `/api`; // 本地

// const applicationName = `/gateway`;
/**
 * 请求基础配置
 */
const baseConfig: any = {
  needCancel: true, // 是否需要在下次请求发生时，对之前的请求进行 abortControl
  isMock: false, // 是否mock方式
  isCheckSpot: false, // 是否是踩点应用的接口
  // isGateway: false,
  mockId: 297, // 其他后台mock的id
  mockSuffix: '', // mock地址后缀
  needHint: true, // 是否需要主动报错
  errorConfig: {
    needHint: true,
    duration: 3, // 报错后多久小时，秒
    showClose: false, // 是否显示关闭按钮，开启后报错弹窗将不会主动消失
  },
};

/**
 * 异常处理
 */
class ApiError extends Error {
  code: string;
  constructor(err: { message?: string; msg?: string; code: string }) {
    const msg: string = err.message || err.msg || '';
    super(msg);
    Object.assign(this, err);
    this.code = err.code;
  }
}

/**
 * 获取header
 * @param version 版本号
 * @param isPost 是否是 Post 请求
 */
export function getHeader(version, isPost = false): any {
  let isShareUrl = false; // 如果shareId参数存在，就代表是处于分享页
  try {
    if (matchQuery(window.location.href, 'shareId')) {
      isShareUrl = true;
    } else if (window.parent?.location?.href && matchQuery(window.parent.location.href, 'shareId')) { // 父窗口嵌套
      isShareUrl = true;
    }
  } catch (err) {}
  // flowLoginToken-前置在需要选择企业的时候获取企业列表的时候header上需要带点击登录之后获取的token
  const apiKey = isShareUrl ? getCookie('flow_share_token') : getCookie('flow_token');
  const apiKeyLogin = getStorage('flowLoginToken');
  const path = window?.location?.pathname;
  const config: any = {
    // Accept: `application/vnd.linhuiba.v${version}+json`,
    // Authorization: apiKey ? `Bearer ${apiKey}` : 'Bearer',
    clientKey: 1000110,
    ...(path ? { 'X-Source-Url': path } : {})
  };
  // 非开发环境生效，开发环境依然正常使用 token（避免无法本地调试）
  if (window.location.href.includes('/pdf/')) { // 如果是pdf文件包下的页面，就默认他是运行在node puppeteer下的页面，此时不允许使用cookie里的token，转而使用url上挂载的token
    config.token = matchQuery(window.location.search, 'token'); // 如果url里没有token，就是undefined，不纠结
  } else if (apiKey || apiKeyLogin) {
    config.token = apiKey || apiKeyLogin;
  }

  return isPost ? { ...config, ...{ 'Content-Type': 'application/json' } } : config;
}

/**
 * 合并请求
 * @param extraConfig
 */
export function mergeConfig(extraConfig: any): RequestConfig {
  const _baseConfig = deepCopy(baseConfig);
  // 为了方便 第三个参数为boolean 值时可控制needHint值
  if (typeof extraConfig === 'boolean') {
    _baseConfig.errorConfig.needHint = extraConfig;
    return _baseConfig;
  } else if (typeof extraConfig.needHint === 'boolean') {
    _baseConfig.errorConfig.needHint = extraConfig.needHint;
  }
  return { ..._baseConfig, ...extraConfig };
}

/**
 * 返回配置后的请求URl
 */
export function getRequestUrl(serviceName: string, config: RequestConfig): string {
  if (URL_REG.test(serviceName)) return serviceName;
  const {
    isMock,
    isCheckSpot,
    isMirage,
    isZeus,
    isLcn,
    isFlowV2,
    isRes,
    isTrust,
    // isLocal,
  } = config;
  if (process.env.NODE_ENV === 'development' && isMock) { // 防止非开发环境使用 mock 接口数据
    return `${mockUrl}${
      config.mockId || baseConfig.mockId
    }${
      config.mockSuffix || baseConfig.mockSuffix || '/api'
    }` + serviceName;
  }
  let prefix = applicationFlow;
  if (isCheckSpot) { // 踩点应用前缀
    prefix = applicationCheckSpot;
  } else if (isMirage) {
    prefix = applicationMirage;
  } else if (isZeus) {
    prefix = applicationZeus;
  } else if (isLcn) {
    prefix = applicationLcn;
  } else if (isFlowV2) {
    prefix = applicationFlowV2;
  } else if (isRes) {
    prefix = appRes;
  } else if (isTrust) {
    prefix = appTrust;
  }
  // else if (isLocal) {
  //   prefix = applicationLocal;
  // }
  return `${prefix}${serviceName}`;
}

/**
 * 错误的请求处理
 * @param err
 * @param config
 */
export function fail(err: any, config: RequestConfig, requestUrl, serviceName: string): null {
  if (axios.isCancel(err)) {
    // !requestUrl && console.log(123, err, config, requestUrl);
    throw new ApiError({
      code: 'cancel',
      msg: `您取消了请求,url:${serviceName}`,
    });
  }

  const needHint = config.errorConfig && config.errorConfig.needHint;
  const duration = config.errorConfig && config.errorConfig.duration;
  const showClose = (config.errorConfig && config.errorConfig.showClose) || false;
  const errorOptions: any = {
    type: 'error',
    duration,
    closable: showClose,
  };
  if (err.response) {
    const {
      status,
      statusText,
      data: { message, errMsg },
    } = err.response;
    switch (status) {
      case 400:
        errorOptions.content = errMsg || 'Bad Request';
        break;
      case 401:
        errorOptions.content = '未登录';
        break;
      case 403:
        errorOptions.content = '未授权';
        break;
      case 404:
        errorOptions.content = '资源不存在';
        break;
      // case 422: // 接口为按规则进行了全部422抛异常，无法控制是否报错，直接移除这个约定
      //   errorOptions.content = message || errMsg;
      //   break;
      case 429:
        errorOptions.content = '休息一会儿，稍后回来';
        break;
      case 500:
        errorOptions.content = '服务器出了小差~';
        break;
      default:
        if (needHint) {
          errorOptions.content = message || errMsg;
        }
        break;
    }
    const statusError = message || errMsg || statusText;
    notifyWechat({
      msgtype: 'text',
      deployedMessage: {
        content: `自有接口异常\nserviceName:${serviceName}\nurl:${window.location.href}\nstatus=${status}\nstatusText:${statusError}`
      }
    });
    // 一些需要用户手动关闭的提示弹窗
    // 约定为420
    // 如，批量操作部分失败的情况
    if (errorOptions.content) {
      const pathname: string = location.pathname;
      if (status === 401 && !pathname.includes('/pdf/') && !pathname.includes('/share/')) {
        // 判断是飞书环境时
        if (isLarkEnv()) {
          // 内部会根据逻辑判断是否要拦截跳转，否则，继续执行退出的逻辑
          handleInterceptInLark();
        } else if (window.navigator.userAgent.includes('wxwork')) { // 企微环境内
          removeCookie('flow_token');
          removeCookie('flow_share_token');
          window.location.replace('/');
          return null;
        } else if (pathname.includes('/imageserve/video')) { // 客流视频服务页面，如 401 则重新登录
          removeCookie('flow_token');
          removeCookie('flow_share_token');
          window.location.replace('/');
          return null;
        }
        logout();
      } else {
        AntdMessage.config({
          maxCount: 1
        });
        if (needHint) {
          AntdMessage.open(errorOptions);
        }
      }
    }
  }
  if (err.code === 'ECONNABORTED' && err.message.indexOf('timeout') !== -1) {
    AntdMessage.error(`服务器响应超时`);
  }
  throw new ApiError(err);
}

/**
 * 成功请求处理
 * remark: 暂时未做处理，预留
 * @param res
 */
export function success(res: any, config: any) {
  const { data } = res;
  if (config.isMock) {
    return data;
  }
  if (isDef(data)) {
    return data;
  } else {
    // AntdMessage.open({
    //   type: 'error',
    //   content: data.msg,
    // });
  }
  throw new ApiError(data);
}

/**
 * 获取 axios 请求配置
 * @param requestConfig
 * @param axiosConfig
 */
export function getAxiosConfig(requestConfig: RequestConfig, axiosConfig: any = {}) {
  const { needCancel = true, customCancelToken } = requestConfig;
  if (customCancelToken) { // 如果设置了手动cancelToken，就使用手动方案
    axiosConfig.customCancelToken = customCancelToken;
  } else if (needCancel) { // 自动方案
    // axios会根据 cancelToken是否存在，进行打断。
    axiosConfig.cancelToken = {};
  }
  if (requestConfig.onceHeaders) {
    axiosConfig.headers = requestConfig.onceHeaders;
  }
  return axiosConfig;
}
